import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import logger from '../../utils/logger';
import './OperationsFinanceLayout.css';
import {
  PAYCOR_SIGNIN_URL,
  FINANCE_NAV_FORMS,
  financeFormPdfUrl
} from '../../constants/financeForms';
import { hasReceiptsAccessFromContextUser } from '../../utils/receiptsAccess';
import { hasContractsAccessFromContextUser } from '../../utils/contractsAccess';

const OperationsFinanceLayout = ({ children }) => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const [userRoles, setUserRoles] = useState({
    isAdmin: false,
    isManager: false,
    isDriver: false,
    isExecutive: false,
    isOperations: false
  });

  useEffect(() => {
    if (currentUser) {
      currentUser
        .getIdTokenResult()
        .then((idTokenResult) => {
          setUserRoles({
            isAdmin:
              idTokenResult.claims.admin === true || idTokenResult.claims.role === 'admin',
            isManager: idTokenResult.claims.manager || false,
            isDriver: idTokenResult.claims.driver || false,
            isExecutive: idTokenResult.claims.executive || false,
            isOperations: idTokenResult.claims.operations === true
          });
        })
        .catch((error) => {
          logger.error('Error getting user claims:', error);
        });
    }
  }, [currentUser]);

  const isActive = (path) => location.pathname === path;
  const isReceiptsActive = location.pathname.startsWith('/receipts');
  const isFinanceFormsMenuActive =
    isActive('/reimbursement-form') ||
    isActive('/cellphone-stipend-form') ||
    isReceiptsActive;
  const isContractsActive = location.pathname.startsWith('/contracts');
  const userLocations =
  currentUser?.locations ||
  currentUser?.claims?.locations ||
  currentUser?.customClaims?.locations ||
  [];
  const isAdmin =
  currentUser?.isAdmin === true ||
  userRoles.isAdmin === true ||
  currentUser?.claims?.admin === true ||
  currentUser?.customClaims?.admin === true ||
  currentUser?.claims?.role === 'admin' ||
  currentUser?.customClaims?.role === 'admin';
  const canViewPestControl = isAdmin || userLocations.includes('Coronado') || userLocations.includes('Eddy');
  const canShowReceipts = hasReceiptsAccessFromContextUser(currentUser);
  const canShowContracts = hasContractsAccessFromContextUser(currentUser);

  return (
    <Container fluid className="operations-finance-layout">
      <Row className="h-100">
        {/* Navigation Sidebar */}
        <Col xs={12} lg={2} className="px-0 operations-sidebar-col">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="p-0">
              <div className="operations-nav">
                {/* Operations Section */}
                <div className="nav-section">
                  <div className="nav-section-title">OPERATIONS</div>
                  <Link
                    to="/incident-reports"
                    className={`nav-item ${isActive('/incident-reports') ? 'active' : ''}`}>

                    <i className="fas fa-exclamation-triangle"></i>
                    <span>Incident Reports</span>
                  </Link>
                  <Link
                    to="/facilities-requests"
                    className={`nav-item ${isActive('/facilities-requests') ? 'active' : ''}`}>

                    <i className="fas fa-tools"></i>
                    <span>Facilities Requests</span>
                  </Link>
                  <Link
                    to="/it-requests"
                    className={`nav-item ${isActive('/it-requests') ? 'active' : ''}`}>

                    <i className="fas fa-laptop-code"></i>
                    <span>IT Requests</span>
                  </Link>
                  {canShowContracts &&
                  <Link
                    to="/contracts"
                    className={`nav-item ${isContractsActive ? 'active' : ''}`}>

                    <i className="fas fa-file-contract"></i>
                    <span>Contracts</span>
                  </Link>
                  }
                  {canViewPestControl &&
                  <Link
                    to="/pest-control-form"
                    className={`nav-item ${isActive('/pest-control-form') ? 'active' : ''}`}>

                    <i className="fas fa-bug"></i>
                    <span>Pest Control</span>
                  </Link>
                  }
                  {(userRoles.isManager || userRoles.isAdmin || userRoles.isExecutive) &&
                  <Link
                    to="/purchasing-requests"
                    className={`nav-item ${isActive('/purchasing-requests') ? 'active' : ''}`}>

                    <i className="fas fa-shopping-cart"></i>
                    <span>Purchasing Requests</span>
                  </Link>
                  }
                  {(userRoles.isDriver || userRoles.isManager || userRoles.isAdmin || userRoles.isExecutive) &&
                  <>
                    <Link
                      to="/daily-vehicle-checklist"
                      className={`nav-item ${isActive('/daily-vehicle-checklist') ? 'active' : ''}`}>

                      <i className="fas fa-car"></i>
                      <span>Daily Vehicle Checklist</span>
                    </Link>
                    <Link
                      to="/driver-inspection-end-of-shift"
                      className={`nav-item ${isActive('/driver-inspection-end-of-shift') ? 'active' : ''}`}>

                      <i className="fas fa-clipboard-check"></i>
                      <span>Driver Inspection End of Shift</span>
                    </Link>
                  </>
                  }
                </div>

                {/* Finance Section */}
                <div className="nav-section">
                  <div className="nav-section-title">FINANCE</div>
                  <div className="nav-submenu nav-submenu-hover">
                    <div
                      className={`nav-item nav-submenu-trigger ${isFinanceFormsMenuActive ? 'active' : ''}`}
                      aria-haspopup="true"
                    >
                      <i className="fas fa-folder-open"></i>
                      <span>Finance Forms</span>
                      <i className="fas fa-chevron-right nav-submenu-chevron" aria-hidden="true" />
                    </div>
                    <div className="nav-submenu-items">
                      <Link
                        to="/reimbursement-form"
                        className={`nav-item nav-subitem ${isActive('/reimbursement-form') ? 'active' : ''}`}>

                        <i className="fas fa-file-invoice-dollar"></i>
                        <span>Reimbursement Requests</span>
                      </Link>
                      <Link
                        to="/cellphone-stipend-form"
                        className={`nav-item nav-subitem ${isActive('/cellphone-stipend-form') ? 'active' : ''}`}>

                        <i className="fas fa-mobile-alt"></i>
                        <span>Cellphone Stipend Agreement</span>
                      </Link>
                      {FINANCE_NAV_FORMS.map((form) =>
                        <a
                          key={form.file}
                          href={financeFormPdfUrl(form.file)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="nav-item nav-subitem">

                          <i className={form.icon}></i>
                          <span>{form.name}</span>
                        </a>
                      )}
                      {canShowReceipts &&
                        <Link
                          to="/receipts"
                          className={`nav-item nav-subitem ${isReceiptsActive ? 'active' : ''}`}>

                          <i className="fas fa-receipt"></i>
                          <span>Receipts</span>
                        </Link>
                      }
                    </div>
                  </div>
                  <Link
                    to="/vendor-forms"
                    className={`nav-item ${isActive('/vendor-forms') ? 'active' : ''}`}>

                    <i className="fas fa-file-alt"></i>
                    <span>Vendor Forms</span>
                  </Link>
                  <a
                    href={PAYCOR_SIGNIN_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nav-item">

                    <i className="fas fa-wallet"></i>
                    <span>Paycor</span>
                  </a>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Main Content Area */}
        <Col xs={12} lg={10} className="px-0 operations-main-col">
          <div className="operations-content">
            {children}
          </div>
        </Col>
      </Row>
    </Container>);

};

export default OperationsFinanceLayout;
